import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Play,
  X,
  Sparkles,
  Zap,
  Bot,
  FileText,
  Bell,
  Star,
  Inbox,
  Languages,
  MessageSquareWarning,
  PhoneOff,
  Clock,
  ClipboardList,
  CheckCircle2,
  Check,
  Instagram,
  Trophy,
  ShieldCheck,
  Wifi,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { captureSourceFromUrl } from "../lib/acquisitionSource";

// ----------------------------------------------------------------------------
// /free — high-conversion landing page for cold/QR/Instagram traffic.
//
// Distinct from the main /landing page in three ways:
//   1. Outcome-first copy (no feature dump in the hero).
//   2. Bilingual — a single flag toggle in the top-right corner flips the
//      entire page between English and Spanish. Same QR code, same URL —
//      no /es route. Selected language is remembered via the existing
//      `LanguageContext` (localStorage).
//   3. Sticky mobile CTA so the button is always one tap away.
//
// All paid routes (register, dashboard) reuse the existing app routes.
// Acquisition-source tracking (?source=…) is captured on mount and is
// language-independent.
// ----------------------------------------------------------------------------

// localStorage flag — set to "1" the first time the user clicks the EN/ES
// toggle. Its presence is what blocks the browser-language auto-detect
// from ever overriding a manual choice on subsequent visits. We keep this
// separate from `tasklio_lang` (which holds the actual value) so the
// auto-detect path doesn't accidentally "lock in" itself.
const LANG_EXPLICIT_KEY = "tasklio_lang_explicit";

// Hero product mock — same emerald palette as the main app dashboard so the
// preview reads as a faithful screenshot of the real product. Takes the
// translator as a prop so it can be rendered from the same `t()` instance
// the page is already using (no extra context lookup).
// Layered composition of REAL Tasklio product screenshots. We use 3 PNGs
// captured from the running app: dashboard, lead detail with AI reply,
// and the financials page. The dominant image is the dashboard; the lead
// detail card floats over the bottom-left to immediately show "AI replies",
// and the financials card peeks from the bottom-right to communicate
// "revenue tracking". This is the single most important trust signal in
// the hero — the page is selling a real, operational product.
const ProductMock = ({ t }) => (
  <div className="relative" aria-hidden="true">
    {/* Main: dashboard screenshot in a faux browser frame */}
    <div className="relative rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
      <div className="h-7 bg-gray-50 border-b border-gray-200 flex items-center gap-1.5 px-3">
        <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        <span className="ml-3 text-[10px] text-gray-400 truncate">
          tasklio.co/dashboard
        </span>
      </div>
      <img
        src="/demo/shots/dashboard-en.png"
        alt={t("free.hero.shotAlt.dashboard")}
        loading="eager"
        decoding="async"
        className="block w-full h-auto select-none"
        draggable="false"
      />
    </div>

    {/* Floating: lead detail with AI reply — anchored bottom-left. Hidden on
        very small screens so the dashboard reads cleanly on mobile. */}
    <div className="hidden sm:block absolute -bottom-6 -left-4 lg:-left-10 w-[55%] max-w-[320px] rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden ring-1 ring-emerald-100/40">
      <div className="h-5 bg-gray-50 border-b border-gray-100 flex items-center gap-1 px-2">
        <span className="w-1.5 h-1.5 rounded-full bg-red-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
      </div>
      <img
        src="/demo/shots/lead-detail-en.png"
        alt={t("free.hero.shotAlt.aiReply")}
        loading="eager"
        decoding="async"
        className="block w-full h-auto select-none"
        draggable="false"
      />
    </div>

    {/* Floating: financials snapshot — anchored top-right with a slight
        downward offset for depth. Hidden on small screens. */}
    <div className="hidden lg:block absolute -top-6 -right-6 w-[40%] max-w-[260px] rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
      <div className="h-5 bg-gray-50 border-b border-gray-100 flex items-center gap-1 px-2">
        <span className="w-1.5 h-1.5 rounded-full bg-red-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
      </div>
      <img
        src="/demo/shots/financials-en.png"
        alt={t("free.hero.shotAlt.financials")}
        loading="eager"
        decoding="async"
        className="block w-full h-auto select-none"
        draggable="false"
      />
    </div>
  </div>
);

// Outcome card (Section 2): big, calm, outcome-first language.
const OutcomeCard = ({ icon: Icon, title, body, testid }) => (
  <div
    className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 transition-all hover:border-emerald-300 hover:shadow-lg hover:-translate-y-[2px]"
    data-testid={testid}
  >
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-emerald-700" />
      </span>
      <h3 className="font-display text-base sm:text-lg font-semibold text-gray-900 leading-snug">
        {title}
      </h3>
    </div>
    <p className="mt-3 text-sm text-gray-600 leading-relaxed">{body}</p>
  </div>
);

// Feature chip card (Section 3): compact icon-led grid.
const FeatureCard = ({ icon: Icon, title, body, testid }) => (
  <div
    className="rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-emerald-300"
    data-testid={testid}
  >
    <span className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
      <Icon className="w-4 h-4 text-emerald-700" />
    </span>
    <h3 className="font-display mt-3 text-sm font-semibold text-gray-900">{title}</h3>
    <p className="mt-1 text-[13px] text-gray-600 leading-snug">{body}</p>
  </div>
);

// Problem item (Section 4): red-leaning icon + tight realistic line.
const ProblemRow = ({ icon: Icon, text }) => (
  <li className="flex items-start gap-3">
    <span className="w-7 h-7 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="w-3.5 h-3.5 text-red-600" />
    </span>
    <span className="text-sm text-gray-700 leading-relaxed">{text}</span>
  </li>
);

export default function FreeLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, setLang } = useT();
  // Authenticated visitors shouldn't be sent to /register (it would replace
  // their token). Mirror Landing.jsx's logic.
  const primaryCtaTo = user ? "/dashboard" : "/register";
  const primaryCtaLabel = user
    ? t("free.hero.primaryCtaAuthed")
    : t("free.hero.primaryCta");

  // Demo modal state mirrors Landing.jsx so the same MP4 + WebM fallback works.
  const [demoOpen, setDemoOpen] = useState(false);
  const [videoErrored, setVideoErrored] = useState(false);
  const videoRef = useRef(null);

  // Persist `?source=...` (or `?utm_source=...`) on first paint so the user
  // can wander around the page — and across to /register — without losing
  // the attribution tag. sessionStorage means the tag goes away when the
  // browser/tab closes, which is the right time-window for signup tracking.
  useEffect(() => {
    captureSourceFromUrl();
  }, []);

  // ----- One-shot browser-language auto-detect -----
  // First-time Spanish-speaking visitors should land directly in ES so the
  // QR / vehicle wrap conversion isn't gated behind a manual click. We
  // honor a user's manual choice forever by only running when the
  // `tasklio_lang_explicit` flag is missing — that flag is set by the
  // EN/ES toggle handler below, so any explicit click locks the preference.
  //
  // Runs once on mount, never reloads the page, and never touches
  // sessionStorage (so `?source=...` attribution is unaffected).
  useEffect(() => {
    try {
      const isExplicit = window.localStorage.getItem(LANG_EXPLICIT_KEY) === "1";
      if (isExplicit) return;
      const browserLang = (navigator.language || navigator.userLanguage || "")
        .toLowerCase();
      if (browserLang.startsWith("es") && lang !== "es") {
        setLang("es");
      }
    } catch {
      // localStorage blocked (private mode etc.) — ignore; default EN is fine.
    }
    // Intentionally [] — this is a one-shot detection on mount, not a
    // reactive effect. `lang` and `setLang` are read fresh inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle handler — wraps setLang so we can mark the choice as "explicit".
  // The flag is what stops the auto-detect effect above from ever
  // overriding a user's manual selection on later visits.
  const selectLang = (next) => {
    try {
      window.localStorage.setItem(LANG_EXPLICIT_KEY, "1");
    } catch {
      /* ignore */
    }
    setLang(next);
  };

  const onDemoOpenChange = (open) => {
    setDemoOpen(open);
    if (!open && videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  };

  const goToRegister = () => navigate(primaryCtaTo);

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="free-landing-page">
      {/* Minimal nav — no main menu, just logo + a tiny flag-based language
          toggle + login + start. The whole point of /free is to remove
          decision friction, so the toggle is small enough not to compete
          with the primary CTA but still discoverable on mobile. */}
      <header className="border-b border-gray-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="free-logo">
            <span className="w-7 h-7 rounded-md bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            <span className="font-display font-bold text-gray-900 tracking-tight">Tasklio AI</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language toggle — two pill buttons, aria-pressed for a11y.
                Flag emojis are intentional here (not in body copy): they're
                the universal visual cue for language pickers across the
                web, especially on mobile where icon-only nav is the norm. */}
            <div
              className="inline-flex items-center rounded-full border border-gray-200 bg-white p-0.5 text-[11px] font-semibold"
              role="group"
              aria-label="Language"
              data-testid="free-lang-toggle"
            >
              <button
                type="button"
                onClick={() => selectLang("en")}
                aria-pressed={lang === "en"}
                className={`px-2 sm:px-2.5 h-7 rounded-full transition-colors inline-flex items-center gap-1 ${
                  lang === "en"
                    ? "bg-green-800 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="free-lang-en"
              >
                <span aria-hidden="true">🇺🇸</span>
                <span>EN</span>
              </button>
              <button
                type="button"
                onClick={() => selectLang("es")}
                aria-pressed={lang === "es"}
                className={`px-2 sm:px-2.5 h-7 rounded-full transition-colors inline-flex items-center gap-1 ${
                  lang === "es"
                    ? "bg-green-800 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="free-lang-es"
              >
                <span aria-hidden="true">🇪🇸</span>
                <span>ES</span>
              </button>
            </div>
            <Link
              to="/login"
              className="text-sm font-medium text-gray-700 hover:text-green-800 hidden sm:inline-block"
              data-testid="free-login-link"
            >
              {t("free.nav.login")}
            </Link>
            <Button
              onClick={goToRegister}
              className="bg-green-800 hover:bg-green-900 text-white h-9 px-3 sm:px-4 text-sm font-semibold rounded-md"
              data-testid="free-nav-start"
            >
              {t("free.nav.startFree")} <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* ====== SECTION 1 — HERO ====== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/40 via-white to-white">
        {/* Soft grid behind the hero — adds depth without competing for attention. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #064e3b 1px, transparent 1px), linear-gradient(to bottom, #064e3b 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:pt-16 sm:pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span
              className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] tracking-[0.18em] uppercase font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full shadow-sm"
              data-testid="free-hero-eyebrow"
            >
              <Sparkles className="w-3.5 h-3.5" /> {t("free.hero.eyebrow")}
            </span>
            <h1
              className="font-display mt-4 text-[2.1rem] sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight text-gray-900 leading-[1.05]"
              data-testid="free-hero-headline"
            >
              {t("free.hero.headlineMain")} <span className="text-emerald-700">{t("free.hero.headlineAccent")}</span>
            </h1>
            <p
              className="mt-5 text-base sm:text-lg text-gray-700 leading-relaxed max-w-2xl"
              data-testid="free-hero-subheadline"
            >
              {t("free.hero.sub")}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={goToRegister}
                className="bg-green-800 hover:bg-green-900 text-white h-12 px-7 text-base font-semibold rounded-lg shadow-md hover:shadow-lg transition-all w-full sm:w-auto"
                data-testid="free-hero-primary-cta"
              >
                {primaryCtaLabel} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                onClick={() => setDemoOpen(true)}
                variant="outline"
                className="h-12 px-7 text-base font-semibold rounded-lg bg-white border-gray-300 text-gray-800 hover:bg-gray-50 w-full sm:w-auto inline-flex items-center justify-center gap-2"
                data-testid="free-hero-watch-demo"
              >
                <Play className="w-4 h-4" /> {t("free.hero.watchDemo")}
              </Button>
            </div>
            <p
              className="mt-3 text-xs text-gray-500"
              data-testid="free-hero-reassurance"
            >
              {t("free.hero.reassurance")}
            </p>
            {/* Pricing clarification — subtle, low visual weight. Tells
                visitors we're a paid product after the trial without
                hijacking the CTA. */}
            <div
              className="mt-1.5 text-[11px] sm:text-xs text-gray-400 leading-snug"
              data-testid="free-hero-pricing-note"
            >
              <p>{t("free.hero.pricingNote1")}</p>
              <p>{t("free.hero.pricingNote2")}</p>
            </div>
            <ul
              className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 max-w-xl"
              data-testid="free-hero-trust"
            >
              {[
                t("free.hero.trust1"),
                t("free.hero.trust2"),
                t("free.hero.trust3"),
                t("free.hero.trust4"),
              ].map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[13px] text-gray-700"
                >
                  <Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                  <span className="leading-snug">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 bg-emerald-200/30 blur-3xl rounded-full -z-10" />
            <ProductMock t={t} />
          </div>
        </div>
      </section>

      {/* ====== SECTION 2 — PROBLEM / SOLUTION ======
           Moved up directly under the hero to mirror the Facebook/Instagram
           ad messaging (people see "stop losing jobs" → CTA in feed →
           land here → see the same idea reinforced immediately). */}
      <section className="bg-white py-14 sm:py-20" data-testid="free-problems">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div>
            <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-red-600">
              {t("free.problems.eyebrow")}
            </span>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-[1.1]">
              {t("free.problems.title")}
            </h2>
            <ul className="mt-6 space-y-3" data-testid="free-problem-list">
              <ProblemRow icon={Instagram} text={t("free.problems.1")} />
              <ProblemRow icon={PhoneOff} text={t("free.problems.2")} />
              <ProblemRow icon={Clock} text={t("free.problems.3")} />
              <ProblemRow icon={MessageSquareWarning} text={t("free.problems.4")} />
              <ProblemRow icon={ClipboardList} text={t("free.problems.5")} />
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 sm:p-8 shadow-sm">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-800 bg-white border border-emerald-200 px-2.5 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> {t("free.solution.eyebrow")}
            </span>
            <h3 className="font-display mt-3 text-2xl font-bold text-gray-900 leading-snug">
              {t("free.solution.title")}
            </h3>
            <p className="mt-3 text-sm text-gray-700 leading-relaxed">
              {t("free.solution.body")}
            </p>
            <ul className="mt-5 space-y-2.5" data-testid="free-solution-list">
              {[
                t("free.solution.1"),
                t("free.solution.2"),
                t("free.solution.3"),
                t("free.solution.4"),
                t("free.solution.5"),
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-800 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ====== SECTION 3 — OUTCOMES ====== */}
      <section className="bg-white py-14 sm:py-20" data-testid="free-outcomes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-emerald-700">
              {t("free.outcomes.eyebrow")}
            </span>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {t("free.outcomes.title")}
            </h2>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
            <OutcomeCard
              icon={Instagram}
              title={t("free.outcome1.title")}
              body={t("free.outcome1.body")}
              testid="free-outcome-1"
            />
            <OutcomeCard
              icon={Zap}
              title={t("free.outcome2.title")}
              body={t("free.outcome2.body")}
              testid="free-outcome-2"
            />
            <OutcomeCard
              icon={Trophy}
              title={t("free.outcome3.title")}
              body={t("free.outcome3.body")}
              testid="free-outcome-3"
            />
            <OutcomeCard
              icon={Wifi}
              title={t("free.outcome4.title")}
              body={t("free.outcome4.body")}
              testid="free-outcome-4"
            />
          </div>
        </div>
      </section>

      {/* ====== SECTION 3 — FEATURES ====== */}
      <section className="bg-gray-50 py-14 sm:py-20 border-y border-gray-100" data-testid="free-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-emerald-700">
              {t("free.features.eyebrow")}
            </span>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {t("free.features.title")}
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <FeatureCard
              icon={Bot}
              title={t("free.feature.replies.title")}
              body={t("free.feature.replies.body")}
              testid="free-feature-replies"
            />
            <FeatureCard
              icon={FileText}
              title={t("free.feature.quotes.title")}
              body={t("free.feature.quotes.body")}
              testid="free-feature-quotes"
            />
            <FeatureCard
              icon={Bell}
              title={t("free.feature.followups.title")}
              body={t("free.feature.followups.body")}
              testid="free-feature-followups"
            />
            <FeatureCard
              icon={Star}
              title={t("free.feature.reviews.title")}
              body={t("free.feature.reviews.body")}
              testid="free-feature-reviews"
            />
            <FeatureCard
              icon={Inbox}
              title={t("free.feature.inbox.title")}
              body={t("free.feature.inbox.body")}
              testid="free-feature-inbox"
            />
            <FeatureCard
              icon={Languages}
              title={t("free.feature.bilingual.title")}
              body={t("free.feature.bilingual.body")}
              testid="free-feature-bilingual"
            />
          </div>
        </div>
      </section>

      {/* ====== SECTION 5 — DEMO VIDEO ====== */}
      <section className="bg-gray-900 py-14 sm:py-20 relative overflow-hidden" data-testid="free-demo-section">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(16,185,129,0.18) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-emerald-300">
            {t("free.demo.eyebrow")}
          </span>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {t("free.demo.title")}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-gray-300 max-w-xl mx-auto">
            {t("free.demo.sub")}
          </p>
          <button
            type="button"
            onClick={() => setDemoOpen(true)}
            className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all px-5 py-4 sm:px-6 sm:py-5 group"
            data-testid="free-demo-play"
          >
            <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-500 flex items-center justify-center group-hover:bg-emerald-400 transition-colors shadow-lg shadow-emerald-500/40">
              <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5" />
            </span>
            <span className="text-left">
              <span className="block text-white font-display font-semibold text-base sm:text-lg">
                {t("free.demo.button.title")}
              </span>
              <span className="block text-emerald-200 text-xs sm:text-sm">
                {t("free.demo.button.sub")}
              </span>
            </span>
          </button>
        </div>
      </section>

      {/* ====== SECTION 6 — FREE TRIAL CTA ====== */}
      <section className="bg-white py-16 sm:py-24" data-testid="free-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-emerald-800 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
            <Sparkles className="w-3 h-3" /> {t("free.finalCta.eyebrow")}
          </span>
          <h2 className="font-display mt-4 text-3xl sm:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.05]">
            {t("free.finalCta.title")}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-gray-700 leading-relaxed">
            {t("free.finalCta.sub")}
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Button
              onClick={goToRegister}
              className="bg-green-800 hover:bg-green-900 text-white h-14 px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              data-testid="free-final-cta-button"
            >
              {t("free.finalCta.button")} <ArrowRight className="w-5 h-5 ml-1.5" />
            </Button>
            <p className="text-xs text-gray-500 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" /> {t("free.finalCta.note1")}
              <span className="text-gray-300">•</span> {t("free.finalCta.note2")}
            </p>
          </div>
        </div>
      </section>

      {/* Minimal footer — keep the page focused */}
      <footer className="border-t border-gray-100 bg-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-2.5 h-2.5" />
            </span>
            <span className="font-display font-semibold text-gray-700">Tasklio AI</span>
            <span className="text-gray-300 mx-1">•</span>
            <span>{t("free.footer.tagline")}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-gray-700" data-testid="free-footer-privacy">{t("free.footer.privacy")}</Link>
            <Link to="/terms" className="hover:text-gray-700" data-testid="free-footer-terms">{t("free.footer.terms")}</Link>
            <Link to="/login" className="hover:text-gray-700" data-testid="free-footer-login">{t("free.footer.login")}</Link>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA — always one tap away. Hidden on sm+ because the
          buttons in the hero and final CTA stay visible on larger screens. */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 sm:hidden border-t border-gray-200 bg-white/95 backdrop-blur p-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        data-testid="free-sticky-cta"
      >
        <Button
          onClick={goToRegister}
          className="bg-green-800 hover:bg-green-900 text-white h-12 w-full text-base font-semibold rounded-lg"
          data-testid="free-sticky-cta-button"
        >
          {t("free.sticky.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>

      {/* Demo video modal — identical mechanics to Landing.jsx so we share the
          same MP4 + WebM assets. */}
      <Dialog open={demoOpen} onOpenChange={onDemoOpenChange}>
        <DialogContent
          className="max-w-4xl w-[calc(100vw-2rem)] p-0 gap-0 bg-black border-0 overflow-hidden rounded-2xl shadow-2xl"
          data-testid="free-demo-video-modal"
        >
          <VisuallyHidden>
            <DialogTitle>Tasklio AI demo video</DialogTitle>
          </VisuallyHidden>
          {videoErrored ? (
            <div
              className="w-full aspect-video bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 flex flex-col items-center justify-center text-center px-6 py-10"
              data-testid="free-demo-video-fallback"
            >
              <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mb-5 ring-1 ring-white/20">
                <Play className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">
                {t("free.demo.fallbackTitle")}
              </h3>
              <p className="mt-3 text-sm sm:text-base text-green-50/80 max-w-md leading-relaxed">
                {t("free.demo.fallbackBody")}
              </p>
              <Button
                onClick={() => {
                  setDemoOpen(false);
                  navigate(primaryCtaTo);
                }}
                className="mt-6 bg-white text-green-900 hover:bg-green-50 h-11 px-6 text-sm font-semibold rounded-lg"
                data-testid="free-demo-fallback-cta"
              >
                {primaryCtaLabel} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              muted
              preload="metadata"
              controlsList="nodownload"
              onError={(e) => {
                const code = e?.currentTarget?.error?.code;
                if (code && code !== 1) setVideoErrored(true);
              }}
              className="w-full h-auto max-h-[80vh] bg-black"
              data-testid="free-demo-video-player"
            >
              <source src="/demo/tasklio-demo.mp4" type="video/mp4" />
              <source src="/demo/tasklio-demo.webm" type="video/webm" />
              <track kind="captions" />
            </video>
          )}
        </DialogContent>
      </Dialog>

      {/* Spacer so the sticky mobile CTA doesn't sit on top of the footer. */}
      <div className="h-20 sm:hidden" aria-hidden="true" />
    </div>
  );
}
